import React, { useRef, useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Star, Quote, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Button } from "../ui/button";
import { ImageLightbox } from "./ImageLightbox";
import { resolveAsset } from "../../lib/assetUrl";

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const TestimonialsLive = ({ testimonials = [], siteContent }) => {
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [lightbox, setLightbox] = useState({ open: false, images: [], startIndex: 0 });
  const whatsappNumber = siteContent?.whatsapp_number || "919056382775";

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.05 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [testimonials.length]);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener("scroll", checkScroll);
      checkScroll();
      return () => el.removeEventListener("scroll", checkScroll);
    }
  }, [testimonials]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: direction === "left" ? -420 : 420, behavior: "smooth" });
    }
  };

  const getOrderLink = (productName) => {
    return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      `Hi Frostyware 3D! I saw a review for "${productName}" and I'd love to order the same!`
    )}`;
  };

  const scrollToProducts = () => {
    document.getElementById("products")?.scrollIntoView({ behavior: "smooth" });
  };

  if (testimonials.length === 0) return null;

  return (
    <>
    <section id="testimonials" ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-200/40 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-sm font-semibold text-sky-500 tracking-wider uppercase">Happy Customers</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">What Our Customers Say</h2>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">Real reviews from real customers. See the quality of our work through their eyes.</p>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <Button variant="outline" size="icon" onClick={() => scroll("left")} className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm shadow-lg shadow-slate-200/50 border-sky-100 hover:bg-sky-50 rounded-full w-10 h-10 hidden md:flex">
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </Button>
          )}
          {canScrollRight && (
            <Button variant="outline" size="icon" onClick={() => scroll("right")} className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm shadow-lg shadow-slate-200/50 border-sky-100 hover:bg-sky-50 rounded-full w-10 h-10 hidden md:flex">
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </Button>
          )}

          <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {testimonials.map((t, index) => {
              const resolvedImage = resolveAsset(t.image);
              return (
              <Card key={t.id} className={`flex-shrink-0 w-[340px] sm:w-[390px] snap-start border border-sky-100/50 bg-white hover:shadow-lg hover:shadow-sky-100/30 transition-all duration-500 overflow-hidden ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: `${index * 120}ms` }}>
                <div
                  className="relative h-56 overflow-hidden group/img cursor-pointer bg-gradient-to-br from-slate-50 to-sky-50/50"
                  onClick={() => setLightbox({ open: true, images: [resolvedImage], startIndex: 0 })}
                >
                  <img src={resolvedImage} alt={t.product} className="w-full h-full object-contain" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                  {/* Zoom hint */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-all duration-300 scale-75 group-hover/img:scale-100 shadow-lg">
                      <ZoomIn className="w-5 h-5 text-slate-700" />
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                    <span className="px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-semibold text-slate-700">
                      {t.product}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); scrollToProducts(); }}
                      className="px-3 py-1 rounded-full bg-sky-500/90 backdrop-blur-sm text-xs font-semibold text-white hover:bg-sky-600 transition-colors opacity-0 group-hover/img:opacity-100 duration-300"
                    >
                      View in Catalog
                    </button>
                  </div>
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <Quote className="w-6 h-6 text-sky-200" />
                    <div className="flex gap-0.5">
                      {Array.from({ length: t.rating || 5 }).map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3">"{t.review}"</p>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-100 to-cyan-50 flex items-center justify-center text-sm font-bold text-sky-600">{t.name?.charAt(0)}</div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                        <div className="text-xs text-slate-400">{t.location}</div>
                      </div>
                    </div>
                    <a
                      href={getOrderLink(t.product)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-600 text-xs font-semibold transition-colors duration-200"
                    >
                      <WhatsAppIcon className="w-3.5 h-3.5" />
                      Order this
                    </a>
                  </div>
                </CardContent>
              </Card>
            );
            })}
          </div>
        </div>
      </div>
    </section>

    <ImageLightbox
      images={lightbox.images}
      startIndex={lightbox.startIndex}
      open={lightbox.open}
      onClose={() => setLightbox((s) => ({ ...s, open: false }))}
    />
    </>
  );
};
